import { useTranslation } from "@i18n";
import React, { useState } from "react";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { markReviewMapper } from "./ReviewsConstants";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";

/**
 * `markReviewMapper` widened on assignment: `userResponse` is a plain `string` at every call site
 * (ReviewDetails passes `userCommentModal.type || ""`), so a lookup can miss. Reading through this
 * alias types the miss as `undefined` instead of asserting the key exists — the previous
 * `as keyof typeof markReviewMapper` hid a `.heading` dereference of `undefined`.
 */
const markReviewCopy: Record<string, (typeof markReviewMapper)[keyof typeof markReviewMapper]> = markReviewMapper;

interface MarkReviewModalProps {
  /** One of `markReviewMapper`'s keys ("approve" | "revoke" | "skip"); callers type it as a plain `string`. */
  readonly userResponse: string;
  readonly closeModal: () => void;
  readonly finalizeUserResponse: (userResponse: string, userComment: string) => void;
  readonly requireMandatoryComment?: boolean;
  readonly isLoadingSkip?: boolean;
  readonly setIsLoadingSkip?: (loading: boolean) => void;
}

function MarkReviewModal({
  userResponse,
  closeModal,
  finalizeUserResponse,
  requireMandatoryComment,
  isLoadingSkip,
  setIsLoadingSkip,
}: MarkReviewModalProps) {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");

  const commentChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComment(e.target.value);
  };

  const confirmMarkReview = () => {
    if ((requireMandatoryComment && comment.trim()) || !requireMandatoryComment) {
      if (typeof setIsLoadingSkip === "function") {
        setIsLoadingSkip(true);
      }
      finalizeUserResponse(userResponse, comment);
    }
  };

  const header = () => <p>{markReviewCopy[userResponse]?.heading}</p>;

  const content = () => (
    <div className="flex flex-col gap-6">
      <p className="text-sm font-normal">{markReviewCopy[userResponse]?.content}</p>
      <div>
        {requireMandatoryComment ? (
          <p className="text-lg font-semibold">{t("Comments*")}</p>
        ) : (
          <p className="text-lg font-semibold">{t("Comments")}</p>
        )}
        <TextFieldComponent placeholder={t("Enter comments")} value={comment} onChange={commentChangeHandler} />
      </div>
    </div>
  );

  const footer = () => {
    const confirmDisabled = requireMandatoryComment && !comment.trim();

    return (
      <div className="flex gap-4">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={confirmMarkReview}
          disabled={confirmDisabled}
          loading={isLoadingSkip}
        >
          {t("Confirm")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
  };
  return (
    <ModalStateDialogComponent
      isVisible
      headerTitle={header()}
      onCancel={closeModal}
      modelContent={content()}
      footerContent={footer()}
    />
  );
}

export default MarkReviewModal;
